// Helper functions for QuestionsModern component

// Type definitions
interface QuestionOption {
  text: string;
  value?: string | number;
}

interface Question {
  id: string | number;
  position: number;
  text: string;
  subtitle?: string;
  subtext?: string;
  input_type: 'text' | 'select' | 'date' | 'radio' | 'multi' | 'nps' | 'waiver';
  answer?: string | number | boolean | string[] | number[];
  options?: QuestionOption[];
  date_format?: string;
}

interface ErrorsMap {
  get: (key: string) => string[] | undefined;
}

export const isAndroid = (): boolean => {
  return window.mobileDetect && window.mobileDetect.ua.indexOf('Android') > -1;
};

export const getAnswerForQuestion = (
  question: Question
): string | number | boolean | string[] | number[] | undefined => {
  return question.answer;
};

export const getAnswersForQuestion = (
  question: Question
): (string | number)[] => {
  return Array.isArray(question.answer) ? question.answer : [];
};

export const transformCheckoutQuestionsErrors = (
  checkoutQuestionsErrors: { answer?: { message?: string } }[] | undefined,
  currentQuestions: Array<{ position?: number }>
): [Record<number, string[]>] | undefined => {
  if (!checkoutQuestionsErrors || !Array.isArray(checkoutQuestionsErrors)) {
    return undefined;
  }

  const positionErrorMap: Record<number, string[]> = {};

  checkoutQuestionsErrors.forEach((itemError, index) => {
    const message = itemError?.answer?.message;
    const questionAtIndex = currentQuestions[index];

    if (message && questionAtIndex && questionAtIndex.position !== undefined) {
      positionErrorMap[questionAtIndex.position] = [message];
    }
  });

  if (Object.keys(positionErrorMap).length === 0) {
    return undefined;
  }
  return [positionErrorMap];
};

export const getErrorsForQuestion = (
  question: Question,
  errors: ErrorsMap
): string[] | undefined => {
  const questionErrors = errors.get('checkout_questions');
  const error = questionErrors && questionErrors[0]?.[question.position];
  if (Array.isArray(error)) {
    return error;
  }
  if (error) {
    return [error];
  }
  return undefined;
};

export const processInputValue = (
  value: string | number | boolean
): string | number | boolean => {
  // waivers will save as 'f' if we don't provide the 'no'
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  return value;
};

export const extractPositionFromName = (name: string): number | null => {
  const position = /-(\d+)/.exec(name)?.[1];
  return position ? parseInt(position, 10) : null;
};

export const createOptionMap = (
  options: QuestionOption[]
): Record<string, string> => {
  return options.reduce((map: Record<string, string>, opt: QuestionOption) => {
    map[`${opt.text} `] = `${opt.text}`;
    return map;
  }, {});
};

export const processMultiSelectValues = (
  values: (string | number | null)[],
  itemValue: string | number,
  itemPosition: number
): (string | number | null)[] => {
  let newValues;
  if (values.includes(itemValue)) {
    const elementIndex = values.indexOf(itemValue);
    values[elementIndex] = null;
    newValues = values;
  } else {
    // To sort answers in order of position
    values[itemPosition] = itemValue;
    newValues = values;
  }
  return newValues;
};

export const getDateMask = (dateFormat: string) => {
  if (dateFormat && typeof dateFormat === 'string') {
    if (dateFormat === 'YYYY/MM/DD') {
      return '1111/11/11';
    }
    return '11/11/1111';
  }
  return '';
};

export const capitalize = (string: string, firstOnly = false) => {
  return (
    string.charAt(0).toUpperCase() +
    (firstOnly ? string.slice(1).toLowerCase() : string.slice(1).toLowerCase())
  );
};
